import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { css, StyleSheet } from 'aphrodite';
import { useHistory, useLocation } from 'react-router-dom';

import BetaPagination from 'components/upb_viewer_beta/BetaPagination';
import BetaProductGrid from 'components/upb_viewer_beta/BetaProductGrid';
import BetaSearchLoading from 'components/upb_viewer_beta/BetaSearchLoading';
import {
  FG_3,
  FONT_BODY,
  FONT_SIZE_SMALL,
  FONT_WEIGHT_MEDIUM,
} from 'components/upb_viewer_beta/css/betaTokens';
import useUPBBetaProducts from 'components/upb_viewer_beta/hooks/useUPBBetaProducts';
import { DEFAULT_UPB_PAGE_SIZE } from 'lib/constants';
import { ProductType } from 'lib/enums/ProductType';
import { addReferrerToUrl } from 'lib/utils/navigationUtils';

/**
 * MfrSearchResults — MSV search view.
 *
 * Search in the Manufacturer Sales Viewer shows a flat, relevance-ranked list of
 * the matching products (not an aggregated manufacturer/brand leaderboard). Each
 * card surfaces the product's Brand / MFC and MPN (BetaProductCard renders these),
 * so you see the products *and* who makes them in one view. Reuses the same
 * semantic product search and card as the UPB viewer for consistency.
 */
function MfrSearchResults({ searchQuery }) {
  const history = useHistory();
  const location = useLocation();

  const [currentPage, setCurrentPage] = useState(1);
  const page = useMemo(() => Math.max(currentPage - 1, 0), [currentPage]);
  const offset = useMemo(() => page * DEFAULT_UPB_PAGE_SIZE, [page]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const { data: { data: products = [], count = 0 } = {}, loading } = useUPBBetaProducts({
    offset,
    limit: DEFAULT_UPB_PAGE_SIZE,
    searchQuery,
  });

  const totalPages = useMemo(() => Math.max(Math.ceil(count / DEFAULT_UPB_PAGE_SIZE), 1), [count]);

  const handleProductClick = useCallback(
    product => {
      const productId = product.id || product.fsa_id;
      const base = `/product-details/${productId}/${ProductType.GLOBAL}`;
      history.push(addReferrerToUrl(base, `${location.pathname}${location.search}`));
    },
    [history, location],
  );

  return (
    <div className={css(styles.page)}>
      <div className={css(styles.head)}>
        <span className={css(styles.count)}>
          {loading
            ? 'Searching…'
            : count === 0
            ? 'No products'
            : `${count.toLocaleString('en-US')} products`}
        </span>
      </div>

      {loading ? (
        <BetaSearchLoading />
      ) : (
        <BetaProductGrid products={products} loading={false} onProductClick={handleProductClick} />
      )}

      {!loading && totalPages > 1 && (
        <BetaPagination
          totalPages={totalPages}
          currentPageNumber={page}
          onPageChange={newPage => setCurrentPage(newPage + 1)}
        />
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    minWidth: 0,
  },

  head: {
    display: 'flex',
    justifyContent: 'flex-end',
  },

  count: {
    fontFamily: FONT_BODY,
    fontWeight: FONT_WEIGHT_MEDIUM,
    fontSize: FONT_SIZE_SMALL,
    color: FG_3,
  },
});

MfrSearchResults.propTypes = {
  searchQuery: PropTypes.string,
};

MfrSearchResults.defaultProps = {
  searchQuery: '',
};

export default MfrSearchResults;
